import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/api/client";
import { LEAD_STATUS, QUOTE_STATUS, CONTRACT_STATUS, PAYMENT_STAGE_STATUS, INVOICE_STATUS, resolveClientType, fmtEur, fmtDate } from "@/lib/domain";
import { StatusBadge, Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowLeft, Mail, Phone, MapPin, Globe, HardHat, FileText, FileSignature,
  Wallet, MessageSquare, FolderOpen, User,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const CLIENT_TYPE_LABEL = { privat: "Privat", firma: "Firma" };

export default function ClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  const load = () => {
    setLoadError(null);
    api.get(`/clients/${id}/overview`)
      .then((r) => { setData(r.data); setNotes(r.data.client.notes || ""); })
      .catch((e) => setLoadError(formatApiErrorDetail(e.response?.data?.detail) || "Не удалось загрузить клиента"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.patch(`/clients/${id}`, { notes });
      toast.success("Заметки сохранены");
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingNotes(false); }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <button onClick={() => nav("/clients")} className="text-slate-400 hover:text-amber-400 text-sm flex items-center gap-1.5">
          <ArrowLeft size={14} /> К списку клиентов
        </button>
        <Card className="p-8 bg-[#131924] border-red-500/30 text-center">
          <div className="text-red-300 mb-3">{loadError}</div>
          <Button data-testid="client-retry-button" onClick={load} variant="outline"
            className="bg-transparent border-[#212b3d] hover:bg-[#1c2434]">Повторить</Button>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="text-slate-400">Загрузка…</div>;
  const { client, objects, quotes, contracts, payment_stages = [], invoices = [], financial_risk = {}, stats } = data;
  const lead = LEAD_STATUS.find((x) => x.value === client.lead_status);
  const primaryContact = (client.contact_persons || [])[0];

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <button data-testid="back-to-clients" onClick={() => nav("/clients")} className="text-slate-400 hover:text-amber-400 text-sm flex items-center gap-1.5">
        <ArrowLeft size={14} /> К списку клиентов
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Chip className="bg-slate-500/15 text-slate-300 border-slate-500/30">{CLIENT_TYPE_LABEL[resolveClientType(client)]}</Chip>
            <Chip className={lead?.cls}>{lead?.label}</Chip>
          </div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight">{client.company || client.name}</h1>
          {client.company && <div className="text-slate-400 text-sm mt-1">{client.name}</div>}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-slate-400 mt-3">
            {client.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {client.phone}</span>}
            {client.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {client.email}</span>}
            {client.website && (
              <a href={client.website.startsWith("http") ? client.website : `https://${client.website}`} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 hover:text-amber-400"><Globe size={13} /> {client.website}</a>
            )}
            {(client.city || client.address) && (
              <span className="flex items-center gap-1.5"><MapPin size={13} /> {client.postal_code} {client.city}{client.address ? `, ${client.address}` : ""}</span>
            )}
          </div>
          {primaryContact && (
            <div className="flex items-center gap-1.5 text-sm text-slate-300 mt-2">
              <User size={13} className="text-amber-400" /> {primaryContact.name}{primaryContact.position ? ` · ${primaryContact.position}` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatBox label="Объекты всего" value={stats.objects_total} />
        <StatBox label="Активные объекты" value={stats.objects_active} accent="amber" />
        <StatBox label="Предложения всего" value={stats.quotes_total} />
        <StatBox label="Принято" value={stats.quotes_accepted} accent="emerald" />
        <StatBox label="Договоры" value={stats.contracts_total} />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#131924] border border-[#212b3d] flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="client-tab-overview">Übersicht</TabsTrigger>
          <TabsTrigger value="objects" data-testid="client-tab-objects">Objekte ({objects.length})</TabsTrigger>
          <TabsTrigger value="quotes" data-testid="client-tab-quotes">Angebote ({quotes.length})</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="client-tab-contracts">Verträge ({contracts.length})</TabsTrigger>
          <TabsTrigger value="finances" data-testid="client-tab-finances">Finanzen</TabsTrigger>
          <TabsTrigger value="communication" data-testid="client-tab-communication">Kommunikation</TabsTrigger>
          <TabsTrigger value="documents" data-testid="client-tab-documents">Dokumente</TabsTrigger>
          <TabsTrigger value="notes" data-testid="client-tab-notes">Notizen</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-5 space-y-4">
          <Card className="p-5 bg-[#131924] border-[#212b3d]">
            <h3 className="font-display font-semibold text-lg mb-3">Контактные лица</h3>
            {(client.contact_persons || []).length === 0 && (
              <div className="text-sm text-slate-500">Дополнительные контактные лица не добавлены.</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(client.contact_persons || []).map((cp, i) => (
                <div key={i} className="rounded-md bg-[#0f1521] border border-[#212b3d] p-3">
                  <div className="text-sm font-medium text-slate-100">{cp.name}</div>
                  {cp.position && <div className="text-xs text-slate-500">{cp.position}</div>}
                  {cp.phone && <div className="text-xs text-slate-400 mt-1 flex items-center gap-1.5"><Phone size={11} /> {cp.phone}</div>}
                  {cp.email && <div className="text-xs text-slate-400 flex items-center gap-1.5"><Mail size={11} /> {cp.email}</div>}
                </div>
              ))}
            </div>
          </Card>
          {client.source && (
            <Card className="p-5 bg-[#131924] border-[#212b3d]">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Источник</div>
              <div className="text-slate-200 text-sm">{client.source}</div>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="objects" className="mt-5">
          {objects.length === 0 ? (
            <EmptyState icon={HardHat} text="У клиента пока нет объектов." />
          ) : (
            <div className="space-y-2">
              {objects.map((o) => (
                <Card key={o.id} data-testid={`client-object-${o.id}`}
                  onClick={() => nav(`/objects/${o.id}`)}
                  className="p-4 bg-[#131924] border-[#212b3d] hover:border-amber-500/30 cursor-pointer flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-100 truncate">{o.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
                      <MapPin size={11} /> {o.postal_code} {o.city}
                      {o.start_date && <span>· {fmtDate(o.start_date)}{o.end_date ? ` → ${fmtDate(o.end_date)}` : ""}</span>}
                    </div>
                  </div>
                  {o.revenue_planned !== undefined && (
                    <div className="text-xs text-slate-500 shrink-0">План: <span className="text-slate-300">{fmtEur(o.revenue_planned)}</span></div>
                  )}
                  <StatusBadge status={o.status} />
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="quotes" className="mt-5">
          {quotes.length === 0 ? (
            <EmptyState icon={FileText} text="У клиента пока нет коммерческих предложений." />
          ) : (
            <div className="space-y-2">
              {quotes.map((q) => (
                <Card key={q.id} data-testid={`client-quote-${q.id}`} className="p-4 bg-[#131924] border-[#212b3d] flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-xs text-slate-500">{q.quote_number}</div>
                    <div className="font-medium text-slate-100 truncate">{q.project_title}
                      {q.reverse_charge_13b && <Chip className="ml-2 bg-purple-500/15 text-purple-300 border-purple-500/30">§13b</Chip>}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{q.date}</div>
                  </div>
                  <Chip className={QUOTE_STATUS[q.status]?.cls}>{QUOTE_STATUS[q.status]?.label}</Chip>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contracts" className="mt-5">
          {contracts.length === 0 ? (
            <EmptyState icon={FileSignature} text="У клиента пока нет договоров." />
          ) : (
            <div className="space-y-2">
              {contracts.map((c) => (
                <Card key={c.id} data-testid={`client-contract-${c.id}`}
                  onClick={() => nav(`/contracts/${c.id}`)}
                  className="p-4 bg-[#131924] border-[#212b3d] hover:border-amber-500/30 cursor-pointer flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1 font-medium text-slate-100 truncate">{c.title}</div>
                  <Chip className={CONTRACT_STATUS[c.status]?.cls}>{CONTRACT_STATUS[c.status]?.label || c.status}</Chip>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="finances" className="mt-5">
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <StatBox label="Offen" value={financial_risk.open_invoices || 0} />
              <StatBox label="Überfällig" value={financial_risk.overdue_invoices || 0} accent="amber" />
              <StatBox label="Offene Summe" value={fmtEur(financial_risk.open_sum || 0)} accent="emerald" />
            </div>
            <FinanceList
              title="Rechnungen"
              empty="У клиента пока нет счетов."
              items={invoices.map((i) => ({
                id: i.id,
                title: `${i.invoice_number} · ${fmtEur(i.brutto)}`,
                meta: `Срок: ${fmtDate(i.due_date)}`,
                chip: INVOICE_STATUS[i.status]?.label || i.status,
                cls: INVOICE_STATUS[i.status]?.cls,
              }))}
            />
            <FinanceList
              title="Zahlungsplan"
              empty="Платёжные этапы ещё не созданы."
              items={payment_stages.map((s) => ({
                id: s.id,
                title: `${s.title} · ${fmtEur(s.amount)}`,
                meta: `Срок: ${fmtDate(s.due_date)}`,
                chip: PAYMENT_STAGE_STATUS[s.status]?.label || s.status,
                cls: PAYMENT_STAGE_STATUS[s.status]?.cls,
              }))}
            />
          </div>
        </TabsContent>
        <TabsContent value="communication" className="mt-5">
          <EmptyState icon={MessageSquare} text="История переписки пока не подключена." />
        </TabsContent>
        <TabsContent value="documents" className="mt-5">
          <EmptyState icon={FolderOpen} text="Документы клиента пока не подключены." />
        </TabsContent>

        <TabsContent value="notes" className="mt-5">
          <Card className="p-4 bg-[#131924] border-[#212b3d]">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Внутренние заметки по клиенту…" className="bg-[#0f1521] border-[#212b3d] min-h-[140px]" />
            <div className="flex justify-end mt-3">
              <Button data-testid="save-client-notes" onClick={saveNotes} disabled={savingNotes}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Сохранить</Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function FinanceList({ title, empty, items }) {
  return (
    <Card className="p-5 bg-[#131924] border-[#212b3d]">
      <h3 className="font-display font-semibold text-lg mb-3">{title}</h3>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500">{empty}</div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="flex items-center justify-between gap-3 rounded-md bg-[#0f1521] border border-[#212b3d] p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-100 truncate">{item.title}</div>
                <div className="text-[11px] text-slate-500">{item.meta}</div>
              </div>
              <Chip className={item.cls || "bg-slate-500/15 text-slate-300 border-slate-500/30"}>{item.chip}</Chip>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function StatBox({ label, value, accent = "slate" }) {
  const cls = { slate: "text-slate-100", amber: "text-amber-400", emerald: "text-emerald-400" }[accent];
  return (
    <div className="rounded-lg border border-[#212b3d] bg-[#131924] p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      <div className={`font-display font-bold text-xl ${cls}`}>{value}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <Card className="p-10 bg-[#131924] border-[#212b3d] text-center text-slate-500">
      <Icon className="mx-auto mb-3 opacity-40" size={28} />
      {text}
    </Card>
  );
}
